<template>
  <div class="login-form">
    <div class="login-iphone">
      <div class="login-title">欢迎访问微芯安智能助手<span style="color:#397CFD;">小程序</span></div>
      <el-image src="images/微信小程序.jpg" alt="logo" />
      <div>微信扫码进入小程序</div>
    </div>
    <div class="login-content-wrapper">
      <!-- 手机验证码登录表单 -->
      <el-form v-if="loginType === 'phone'" :model="phoneLoginForm" ref="phoneLoginFormRef" class="login-form-content">
        <el-form-item prop="phone">
          <div class="phone-input-wrapper">
            <el-input v-model="phoneLoginForm.mobile" size="large" clearable type="text" placeholder="请输入手机号">
            </el-input>
            <el-button
                type="primary"
                link
                :disabled="countdown > 0"
                @click="sendSmsCode"
            >
              {{ countdown > 0 ? `${countdown}s后重新发送` : '发送验证码' }}
            </el-button>
          </div>
        </el-form-item>
        <el-form-item prop="smsCode">
          <el-input v-model="phoneLoginForm.verCode" size="large" clearable type="text" placeholder="请输入验证码">
          </el-input>
        </el-form-item>
        <el-form-item style="width: 100%">
          <el-button
              :loading="loginLoading"
              class="logBtn"
              size="large"
              type="primary"              style="width: 100%;"
              @click="handlePhoneLogin(phoneLoginFormRef)"
          >
            <span v-if="!loginLoading">登 录</span>
            <span v-else>登 录 中...</span>
          </el-button>
        </el-form-item>
        <el-form-item>
          <div class="switch-login-type">
            <el-button type="primary" link @click="switchLoginType('account')">
              密码登录
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <!-- 账号密码登录表单 -->
      <el-form v-else :model="loginForm" ref="loginFormRef" class="login-form-content">
        <el-form-item prop="username">
          <el-input v-model="loginForm.mobile" size="large" clearable type="text" placeholder="请输入用户名">
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input type="password" v-model="loginForm.password" size="large" clearable show-password placeholder="请输入密码">
          </el-input>
        </el-form-item>
        <el-form-item style="width: 100%">
          <el-button :loading="loginLoading" class="logBtn" size="large" type="primary" style="width: 100%;"
                     @click="handleLogin(loginFormRef)">
            <span v-if="!loginLoading">登 录</span>
            <span v-else>登 录 中...</span>
          </el-button>
        </el-form-item>
        <el-form-item>
          <div class="switch-login-type">
            <el-button type="primary" link @click="switchLoginType('phone')">
              手机验证码登录
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, reactive} from 'vue'
import useUserStore from '@/stores/user'
import AES from "@/utils/AES";
import { ElMessage } from 'element-plus'
import type {FormInstance} from 'element-plus'
// 接口调用
import {getPhoneCode, phoneLogin,accountLogin} from "@/AIApi";


let emit = defineEmits(['onClose'])
// 登录类型: 'phone' | 'account'
const loginType = ref<'phone' | 'account'>('phone')
const countdown = ref(0)
let timer: NodeJS.Timeout | null = null

// 手机验证码登录表单
const phoneLoginFormRef = ref<FormInstance>()
const phoneLoginForm = reactive({
  mobile: '',
  verCode: ''
})

const userStore = useUserStore()
const loginLoading = ref(false);
const loginFormRef = ref<FormInstance>()
const loginForm = reactive({
  mobile: '',
  password: ''
});

// 切换登录方式
const switchLoginType = (type: 'phone' | 'account') => {
  loginType.value = type
}

// 发送短信验证码
const sendSmsCode = async () => {
  if (!phoneLoginForm.mobile) {
    ElMessage.warning('请先输入手机号')
    return
  }

  // 手机号格式验证
  const phoneReg = /^1[3-9]\d{9}$/
  if (!phoneReg.test(phoneLoginForm.mobile)) {
    ElMessage.error('请输入正确的手机号')
    return
  }

  try {
    // 调用发送短信验证码接口
    const [err, res] = await awaitTo(getPhoneCode({ mobile: phoneLoginForm.mobile }))

    if (res && res.code === 1) {
      ElMessage.success('验证码已发送')
      // 开始倒计时
      countdown.value = 60
      timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer!)
          timer = null
        }
      }, 1000)
    }else{
      ElMessage.error(res?.msg || '发送验证码失败')
    }
  } catch (error) {
    ElMessage.error('发送验证码失败')
  }
}

// 手机验证码登录
const handlePhoneLogin = async (formEl: FormInstance | undefined) => {
  if (!formEl) return

  // 验证表单
  if (!phoneLoginForm.mobile || !phoneLoginForm.verCode) {
    ElMessage.error('请填写完整信息')
    return
  }
  // 手机号格式验证
  const phoneReg = /^1[3-9]\d{9}$/
  if (!phoneReg.test(phoneLoginForm.mobile)) {
    ElMessage.error('请输入正确的手机号')
    return
  }
  // 验证码格式验证（通常为6位数字）
  if (!/^\d{6}$/.test(phoneLoginForm.verCode)) {
    ElMessage.error('请输入正确的验证码')
    return
  }

  loginLoading.value = true

  try {
    // 调用手机验证码登录接口
    const [err, res] = await awaitTo(phoneLogin({
      mobile: phoneLoginForm.mobile,
      verCode: phoneLoginForm.verCode
    }))

    if (!err && res && res.code === 200) {
      let loginMsg = res.data

      ElMessage.success('登录成功')

      // 设置登录成功状态
      userStore.setLoggedInState(true)
      userStore.setUserState("user_token", loginMsg.token)
      userStore.setUserState("user", loginMsg.userInfo)
      userStore.setUserState("timestemp", loginMsg.timestemp)

      setTimeout(() => {
        loginLoading.value = false
        emit('onClose')
      }, 300)
    } else {
      loginLoading.value = false
      ElMessage.error(res?.msg || '登录失败，请重试')
    }
  } catch (error) {
    console.error('登录错误:', error)
    loginLoading.value = false
    ElMessage.error('登录失败，请稍后重试')
  }
}

const handleLogin = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  await formEl.validate(async (valid: boolean, fields: any) => {
    if (valid) {
      loginForm.password = AES.encrypt(loginForm.password)
      console.log("loginForm", loginForm)
      loginLoading.value = true;
      // 登录逻辑 - 使用 accountLogin 接口
      const [err, res] = await awaitTo(accountLogin(loginForm))
      console.log("err, res", err, res)
      if (res.code == 200) {
        let loginMsg = res.data
        ElMessage.success('登录成功');
        // 设置登录成功状态
        userStore.setLoggedInState(true)
        userStore.setUserState("user_token", loginMsg.token)
        userStore.setUserState("user", loginMsg.userInfo)
        userStore.setUserState("timestemp", loginMsg.timestemp)

        setTimeout(() => {
          loginLoading.value = false
          emit('onClose')
        }, 300)
      }else{
        loginForm.password = AES.decrypt(loginForm.password)
        loginLoading.value = false
        ElMessage.error(res.msg);
      }
    } else {
      console.log('error submit!', fields)
      loginLoading.value = false
      ElMessage.error('登录验证失败');
      return false;
    }
  })
}
</script>

<style scoped>

/* Login Dialog */
.login-form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 30px;
}
.login-iphone{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.login-iphone .el-image{
  width: 177px;
  height: 177px;
  margin: 15px 0 13px 0;
}
.el-form{
  width:100%;
  margin-top: 20px;
}
.login-content-wrapper {
  min-height: 310px;
  min-width: 307px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.login-form-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.login-title {
  font-size: 16px;
  font-weight: 600;
  color: #111625;
  margin-bottom: 10px;
  text-align: center;
}
.imgCode{
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.captcha-placeholder{
  width: 120px;
  cursor: pointer;
  color: #999;
  padding:8px;
}
.captcha-placeholder{
  width: 120px;
  cursor: pointer;
  color: #999;
  padding:8px;
}
/* 切换登录方式按钮 */
.switch-login-type {
  width: 100%;
  text-align: right;
  margin-top: 4px;
}
.switch-login-type .el-button {
  font-size: 14px;
}
/* 手机号输入框包装器 */
.phone-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 0 12px;
  background-color: #fff;
  transition: border-color 0.3s;
}
.phone-input-wrapper:hover {
  border-color: #c0c4cc;
}
.phone-input-wrapper:focus-within {
  border-color: #397CFD;
}
.phone-input-wrapper .el-input {
  flex: 1;
}
.phone-input-wrapper .el-input :deep(.el-input__wrapper) {
  box-shadow: none !important;
  border: none !important;
  padding: 0;
}
.phone-input-wrapper .el-input :deep(.el-input__wrapper):hover {
  box-shadow: none !important;
}
/* 所有输入框统一样式 */
:deep(.el-input__wrapper) {
  border-radius: 8px;
}
</style>
